import { Backdrop, Box, Paper, Typography } from "@mui/material";
import { CSSObject, styled, Theme } from "@mui/material/styles";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/hooks/reduxHooks";
import resolveSidePanelLayout from "~/layout/resolveSidePanelLayout";
import { closePanel, selectPanelOpen } from "~/store/layoutSlice";

const drawerWidthMin = 300;
const drawerWidthDefault = 600;

const sheetTransition = (theme: Theme, open: boolean): string =>
	open
		? theme.transitions.create("transform", {
				duration: theme.transitions.duration.enteringScreen,
				easing: theme.transitions.easing.easeOut
			})
		: [
				theme.transitions.create("transform", {
					duration: theme.transitions.duration.leavingScreen,
					easing: theme.transitions.easing.sharp
				}),
				theme.transitions.create("visibility", { delay: theme.transitions.duration.leavingScreen, duration: 0 })
			].join(",");

const overlaySlot: CSSObject = {
	left: 0,
	overflowX: "clip",
	pointerEvents: "none",
	position: "absolute",
	right: 0,
	top: 0,
	width: "auto"
};

interface DrawerSlotProps {
	isMobile: boolean;
	mobileViewportScroll: boolean;
	open: boolean;
	width: number;
}

const DrawerSlot = styled(Box, {
	shouldForwardProp: (prop) =>
		prop !== "open" && prop !== "width" && prop !== "isMobile" && prop !== "mobileViewportScroll"
})<DrawerSlotProps>(({ isMobile, mobileViewportScroll, open, theme, width }) => ({
	flexShrink: 0,
	marginRight: open ? theme.spacing(1) : 0,
	width: open ? width : 0,
	zIndex: 1000,

	...(mobileViewportScroll
		? isMobile
			? {
					...overlaySlot,
					height: "100dvh",
					maxHeight: "100dvh"
				}
			: {
					height: "100%",
					position: "relative"
				}
		: {
				height: "100%",
				[theme.breakpoints.down("md")]: overlaySlot,
				[theme.breakpoints.up("md")]: {
					position: "relative"
				}
			})
}));

interface DrawerSheetProps {
	open: boolean;
	width: number;
}

const DrawerSheet = styled(Paper, {
	shouldForwardProp: (prop) => prop !== "open" && prop !== "width"
})<DrawerSheetProps>(({ open, theme, width }) => ({
	borderLeft: `1px solid ${theme.palette.divider}`,
	height: "100%",
	overflowY: "auto",
	pointerEvents: "auto",
	position: "absolute",
	right: 0,
	top: 0,
	transform: open ? "none" : "translateX(100%)",
	transition: sheetTransition(theme, open),
	visibility: open ? "visible" : "hidden",
	width
}));

const ResizeHandle = styled(Box)(({ theme }) => ({
	"&:active": {
		backgroundColor: theme.palette.primary.main,
		opacity: 0.5
	},
	"&:hover": {
		backgroundColor: theme.palette.primary.main,
		opacity: 0.3
	},
	backgroundColor: "transparent",
	bottom: 0,
	cursor: "col-resize",
	left: 0,
	position: "absolute",
	top: 0,
	width: "8px",
	zIndex: 1001
}));

interface SidePanelProps {
	content: ReactNode;
	defaultWidth?: number;
	mobileViewportScroll?: boolean;
}

const SidePanel = ({ content, defaultWidth, mobileViewportScroll = false }: SidePanelProps) => {
	const { t } = useTranslation("common");
	const dispatch = useAppDispatch();
	const open = useAppSelector(selectPanelOpen);
	const isMobile = useAppSelector((state) => !state.layout.isWideScreen);
	const effectiveDefault = defaultWidth ?? drawerWidthDefault;
	const [panelWidth, setPanelWidth] = useState<number>(effectiveDefault);
	const [isResizing, setIsResizing] = useState(false);
	const panelRef = useRef<HTMLDivElement>(null);

	// Reset to default width when panel opens with new content
	// But respect window width as maximum
	useEffect(() => {
		if (open && content) {
			const maxWidth = window.innerWidth - 50; // Leave some margin
			const newWidth = Math.min(effectiveDefault, maxWidth);
			setPanelWidth(Math.max(newWidth, drawerWidthMin)); // Don't go below minimum
		}
	}, [open, content, effectiveDefault]);

	const handleMouseDown = useCallback((e: React.MouseEvent) => {
		e.preventDefault();
		setIsResizing(true);
	}, []);

	const handleMouseMove = useCallback(
		(e: MouseEvent) => {
			if (!isResizing) return;

			const newWidth = window.innerWidth - e.clientX;
			const maxWidth = window.innerWidth - 300; // Leave room for menu
			if (newWidth >= drawerWidthMin && newWidth <= maxWidth) {
				setPanelWidth(newWidth);
			}
		},
		[isResizing]
	);

	const handleMouseUp = useCallback(() => {
		if (isResizing) {
			setIsResizing(false);
		}
	}, [isResizing]);

	useEffect(() => {
		if (isResizing) {
			document.addEventListener("mousemove", handleMouseMove);
			document.addEventListener("mouseup", handleMouseUp);
			// Prevent text selection while resizing
			document.body.style.userSelect = "none";
			document.body.style.cursor = "col-resize";
		} else {
			document.body.style.userSelect = "";
			document.body.style.cursor = "";
		}

		return () => {
			document.removeEventListener("mousemove", handleMouseMove);
			document.removeEventListener("mouseup", handleMouseUp);
			document.body.style.userSelect = "";
			document.body.style.cursor = "";
		};
	}, [isResizing, handleMouseMove, handleMouseUp]);

	// Handle window resize - adjust sidebar width if it exceeds window width
	useEffect(() => {
		const handleWindowResize = () => {
			if (open) {
				const maxWidth = window.innerWidth - 50; // Leave some margin
				if (panelWidth > maxWidth) {
					setPanelWidth(Math.max(maxWidth, drawerWidthMin));
				}
			}
		};

		window.addEventListener("resize", handleWindowResize);
		return () => window.removeEventListener("resize", handleWindowResize);
	}, [open, panelWidth]);

	const isOpen = open && content !== null;
	const { isMobileOverlay, showDismissBackdrop } = resolveSidePanelLayout({ isMobile, isOpen, mobileViewportScroll });

	const handleBackdropClick = () => {
		dispatch(closePanel());
	};

	return (
		<>
			<Backdrop
				data-side-panel="true"
				onClick={handleBackdropClick}
				open={showDismissBackdrop}
				sx={mobileViewportScroll ? { zIndex: 999 } : { display: { md: "none", xs: "block" }, zIndex: 999 }}
			/>
			<DrawerSlot isMobile={isMobile} mobileViewportScroll={mobileViewportScroll} open={isOpen} width={panelWidth}>
				<DrawerSheet
					aria-hidden={!isOpen}
					aria-label={isOpen ? t("shell.sidePanel.label") : undefined}
					data-mobile={isMobileOverlay ? "true" : undefined}
					data-side-panel="true"
					elevation={0}
					open={isOpen}
					ref={panelRef}
					role={isOpen ? "region" : undefined}
					tabIndex={isOpen ? 0 : -1}
					width={panelWidth}
				>
					{isOpen && <ResizeHandle onMouseDown={handleMouseDown} />}
					{content || <Typography variant="body1">{t("shell.sidePanel.noContent")}</Typography>}
				</DrawerSheet>
			</DrawerSlot>
		</>
	);
};

export default SidePanel;
